import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import {
  CloudCustomer,
  CreateCloudAccountDialogData,
  CreateDevOpsActionDialogData,
  DevOpsActionAndParameter,
  ErmDevOpsApiClientService,
} from 'erm-sdk';
import * as _ from 'lodash';
import { CreateAccountComponent } from '../create-account/create-account.component';
import dayjs from 'dayjs';

@Component({
  standalone: false,
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.scss'],
})
export class CustomerListComponent implements OnInit {
  isLoading = false;
  gridOptions: GridOptions;
  gridApi?: GridApi;
  selectedCustomer?: CloudCustomer;
  supportedRegions?: string[];
  customers?: CloudCustomer[];

  selectedDevOpsAction?: DevOpsActionAndParameter;
  devOpsActions?: DevOpsActionAndParameter[];
  createDevOpsActionDialogData?: CreateDevOpsActionDialogData;

  @ViewChild('agGrid') agGrid!: AgGridAngular;

  constructor(
    private ermDevOpsApiClient: ErmDevOpsApiClientService,
    private router: Router,
    private matdialog: MatDialog
  ) {
    this.gridOptions = {
      columnDefs: this.createColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: (event) => {
        this.selectedCustomer = _.first(
          event.api.getSelectedNodes().map((x) => x.data)
        );
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
      },
      onGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
      },
    };
  }

  async ngOnInit(): Promise<void> {
    try {
      this.isLoading = true;

      // Get customers
      const getCustomersResponse =
        await this.ermDevOpsApiClient.getCloudCustomers();
      this.customers =
        getCustomersResponse.body?.cloudCustomers || new Array<CloudCustomer>();

      // Get DevOps Actions
      const getDevopsActionsAndParameter =
        await this.ermDevOpsApiClient.getDevOpsActionsAndParameters();
      this.devOpsActions =
        getDevopsActionsAndParameter.body?.devOpsActionsAndParameters ||
        new Array<DevOpsActionAndParameter>();

      if (this.devOpsActions) {
        this.devOpsActions = this.devOpsActions.filter(
          (x) => x.actionScope == 'Customer'
        );
      }

      // Initialize grid
      this.gridApi?.setGridOption('rowData', this.customers);
    } finally {
      this.isLoading = false;
    }
  }

  private createColumnDefs(): ColDef[] {
    const router = this.router;
    return [
      {
        headerName: 'Customer Id',
        field: 'customerId',
        cellRenderer: (params: any) => {
          const url = router.createUrlTree(['erm/customers', params.value]);
          return `<a href="#${url}">${params.value}</a>`;
        },
      },
      { headerName: 'Name', field: 'name' },
      { headerName: 'Description', field: 'description', width: 300 },
      {
        headerName: 'Connect Account Id',
        field: 'connectAccountId',
        width: 300,
      },
      {
        headerName: 'Created',
        field: 'creationTime',
        valueFormatter: (params: any) => {
          return dayjs(params.value).format('YYYY-MM-DD HH:mm:ss');
        },
      },
    ];
  }

  async openCreateAccount(): Promise<void> {
    try {
      this.isLoading = true;

      if (!this.supportedRegions) {
        const getSupportedRegions =
          await this.ermDevOpsApiClient.getSupportedRegions();
        this.supportedRegions = getSupportedRegions.body?.regions || [];
      }

      this.matdialog.open(CreateAccountComponent, {
        data: {
          customerId: this.selectedCustomer?.customerId,
          regions: this.supportedRegions,
        } as CreateCloudAccountDialogData,
      });
    } finally {
      this.isLoading = false;
    }
  }

  canCreateAccount(): boolean {
    return !this.selectedCustomer;
  }
}
